import {
  BodyText,
  CaptionText,
  FlexColumn,
  FlexRow,
  SvgIcon,
  useClickAndTouchAway,
} from '@make-software/cspr-design';
import { Trans, useTranslation } from 'react-i18next';
import React, { PropsWithChildren, useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import ReactDOM from 'react-dom';
import Big from 'big.js';
import { Button } from '@block-explorer/ui';

const MOBILE_MODAL_WIDTH = 220;

const StyledButton = styled(Button)(({ theme }) =>
  theme.withMedia({
    width: '67px',
    fontWeight: theme.typography.fontWeight.medium,
    minHeight: 24,
    color: theme.styleguideColors.contentPrimary,
    padding: ['4px 10px'],
    div: {
      marginRight: 8,
    },
    ':hover': {
      color: theme.styleguideColors.contentRed,
      svg: {
        path: {
          fill: theme.styleguideColors.contentRed,
        },
      },
    },
  })
);

const SvgIconWrapper = styled('div')<{ isFilterSet?: boolean }>(
  ({ theme, isFilterSet }) =>
    theme.withMedia({
      position: 'relative',
      display: 'inline-flex',

      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,

        width: '6px',
        height: '6px',
        borderRadius: '50%',
        backgroundColor: theme.styleguideColors.contentRed,
        border: `3px solid ${theme.styleguideColors.fillSecondary}`,
        boxSizing: 'content-box',

        transform: `translate(50%, -50%) scale(${isFilterSet ? 1 : 0})`,
        transition: 'transform 0.2s ease',
      },
    })
);
const StyledSvgIcon = styled(SvgIcon)<{ isActive?: boolean }>(
  ({ theme, isActive }) => ({
    cursor: 'pointer',
    path: {
      fill: isActive
        ? theme.styleguideColors.contentRed
        : theme.styleguideColors.contentTertiary,
    },
  })
);

const StyledFlexRow = styled(FlexRow)(({ theme }) => ({
  position: 'relative',
}));

const FilterModalContainer = styled(FlexColumn)<{ isOpen: boolean }>(
  ({ theme, isOpen }) =>
    theme.withMedia({
      display: isOpen ? 'flex' : 'none',
      marginTop: 4,
      borderRadius: theme.borderRadius.base,
      position: 'absolute',
      top: '20px',
      zIndex: 10,
      backgroundColor: theme.styleguideColors.backgroundPrimary,
      justifyContent: 'center',
      width: [MOBILE_MODAL_WIDTH, '276px'],
      boxShadow: theme.boxShadow.block,
      padding: '20px 16px 16px',
    })
);

const ClearButton = styled(BodyText)<{ disabled?: boolean }>(
  ({ theme, disabled }) => ({
    color: disabled
      ? theme.styleguideColors.contentTertiary
      : theme.styleguideColors.contentRed,
    fontWeight: 400,
    cursor: 'pointer',
  })
);

const SubmitButton = styled(BodyText)<{ disabled?: boolean }>(
  ({ theme, disabled }) => ({
    color: disabled
      ? theme.styleguideColors.contentTertiary
      : theme.styleguideColors.contentBlue,
    fontWeight: 600,
    cursor: 'pointer',
  })
);

export interface FilterProps {
  filters: Record<string, string | null>;
  setFilters: (filters: Record<string, string | null>) => void;
}

interface FilterContainerProps {
  isFilterSet: boolean;
  onSetFilters: () => void;
  onResetFilters: () => void;
  onResetErrors: () => void;
  disabled: boolean;
  isFullView?: boolean | undefined;
}

const FilterContainer = ({
  isFilterSet,
  onSetFilters,
  onResetFilters,
  onResetErrors,
  disabled,
  children,
  isFullView,
}: PropsWithChildren<FilterContainerProps>) => {
  const { t } = useTranslation();
  const [isOpen, setOpened] = useState(false);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const modalRef = useRef<HTMLDivElement>(null);

  const handleResetErrorsAndClose = () => {
    setOpened(false);
    onResetErrors();
  };

  const { ref } = useClickAndTouchAway({
    callback: (event) => {
      if (modalRef.current?.contains(event.target)) return;
      handleResetErrorsAndClose();
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    window.addEventListener('resize', handleResetErrorsAndClose);
    return () =>
      window.removeEventListener('resize', handleResetErrorsAndClose);
  }, [isOpen, handleResetErrorsAndClose]);

  const handleShowModal = () => {
    if (!isOpen && ref.current) {
      const rect = ref.current.getBoundingClientRect();

      const isPositionFitModal = Big(window.screen.width)
        .minus(rect.left)
        .lte(MOBILE_MODAL_WIDTH);
      setModalPosition({
        top: rect.bottom + window.scrollY + 4,
        left: isPositionFitModal
          ? rect.left - MOBILE_MODAL_WIDTH
          : rect.left + window.scrollX,
      });
    }
    setOpened((prev) => !prev);
  };

  const handleResetFilters = () => {
    if (!disabled) onResetFilters();
  };

  const handleSetFilters = () => {
    if (!disabled) onSetFilters();
  };

  const modal = ReactDOM.createPortal(
    <FilterModalContainer
      ref={modalRef}
      isOpen={isOpen}
      itemsSpacing={12}
      style={{
        top: modalPosition.top,
        left: modalPosition.left,
      }}
    >
      {children}
      <FlexRow justify={'space-between'}>
        <ClearButton
          size={1}
          onClick={handleResetFilters}
          disabled={disabled}
          tabIndex={0}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') handleResetFilters();
          }}
        >
          <Trans t={t}>Clear</Trans>
        </ClearButton>
        <SubmitButton
          size={1}
          onClick={handleSetFilters}
          disabled={disabled}
          tabIndex={0}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') handleSetFilters();
          }}
        >
          <Trans t={t}>Filter</Trans>
        </SubmitButton>
      </FlexRow>
    </FilterModalContainer>,
    document.body
  );

  const filterIcon = (
    <SvgIconWrapper isFilterSet={isFilterSet} ref={ref}>
      <StyledSvgIcon
        src={'assets/icons/ic-filter.svg'}
        role={'img'}
        alt={'Filter icon'}
        onClick={handleShowModal}
        tabIndex={0}
        onKeyDown={(ev) => {
          if (ev.key === 'Enter') handleShowModal();
        }}
        isActive={isOpen}
      />
    </SvgIconWrapper>
  );

  return (
    <StyledFlexRow>
      {isFullView ? (
        <StyledButton
          color={'secondaryRed'}
          onClick={handleShowModal}
          tabIndex={0}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') handleShowModal();
          }}
        >
          {filterIcon}
          <CaptionText size={2}>
            <Trans t={t}>Filter</Trans>
          </CaptionText>
        </StyledButton>
      ) : (
        filterIcon
      )}
      {isOpen ? modal : null}
    </StyledFlexRow>
  );
};

export default FilterContainer;
